import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { Settings, LogOut, RefreshCw, Download, FileText, Eye, Sparkles, Activity, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import AddAccountModal from "@/components/AddAccountModal";
import RemoveAccountModal from "@/components/RemoveAccountModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtElapsed(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r.toString().padStart(2, "0")}s` : `${s}s`;
}

const STAGE_LABEL = {
  full: "Full (harvest + synthesis)",
  synthesis_only: "Synthesis",
  matrix_only: "Matrix",
};

// Chunk 4.5 — live LLM pipeline health widget. Polls /api/admin/pipeline/status
// every 3s. Surfaces active task with elapsed time + queue depth + last
// completed run. Read-only; the underlying single-flight semaphore lives in
// server.py.
//
// `onFullRunComplete` fires once per `full`-stage completion (harvest + synthesis)
// so callers can refresh dependent UI (Accounts table source/dead-link counts).
function PipelineHealthWidget({ onFullRunComplete }) {
  const [state, setState] = useState({ active: null, queue: [], queue_depth: 0, last_completed: null });
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(null);
  const [seenCompletionKey, setSeenCompletionKey] = useState(null);

  // Optimistic local elapsed counter — avoids stutter between polls.
  const [tickNow, setTickNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await API.get("/admin/pipeline/status");
      const data = r.data || { active: null, queue: [], queue_depth: 0, last_completed: null };
      setState(data);
      setErr(null);
      // Auto-refresh trigger: when a `full`-stage run finishes (only stage that
      // changes the Accounts table — synthesis_only/matrix_only don't move
      // source counts or dead-link percentages).
      const lc = data.last_completed;
      if (lc && lc.stage === "full" && lc.ended_at && onFullRunComplete) {
        const key = `${lc.account}|${lc.ended_at}`;
        if (key !== seenCompletionKey) {
          setSeenCompletionKey(key);
          // First load: don't fire — caller already loaded fresh data on mount.
          if (seenCompletionKey !== null) {
            onFullRunComplete(lc);
          }
        }
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  }, [onFullRunComplete, seenCompletionKey]);

  useEffect(() => {
    fetchOnce();
    const fast = state.active || (state.queue_depth > 0);
    const id = setInterval(fetchOnce, fast ? 3000 : 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnce, state.active?.account, state.queue_depth]);

  const active = state.active;
  // Compute live elapsed seconds locally, fall back to server value.
  let liveElapsed = active?.elapsed_seconds ?? null;
  if (active?.started_at) {
    try {
      const started = new Date(active.started_at).getTime();
      liveElapsed = Math.max(0, Math.floor((tickNow - started) / 1000));
    } catch { /* keep server value */ }
  }

  const idle = !active && (state.queue_depth === 0);
  const lastCompletedLine = (() => {
    const lc = state.last_completed;
    if (!lc) return null;
    let elapsed = null;
    try {
      const a = new Date(lc.started_at).getTime();
      const b = new Date(lc.ended_at).getTime();
      elapsed = Math.max(0, Math.floor((b - a) / 1000));
    } catch { /* noop */ }
    return `Last: ${STAGE_LABEL[lc.stage] || lc.stage} · ${lc.account} · ${lc.status} · ${elapsed != null ? fmtElapsed(elapsed) : "—"}`;
  })();

  return (
    <section
      className="bg-white border border-zinc-200 mb-6"
      data-testid="pipeline-health-widget"
    >
      <div className="px-6 py-4 flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <div
            className={`flex items-center justify-center w-9 h-9 ${
              idle ? "bg-zinc-50 text-zinc-700" : "bg-amber-50 text-amber-700"
            }`}
            aria-hidden="true"
          >
            <Activity
              className={`w-4 h-4 ${idle ? "" : "animate-pulse"}`}
              strokeWidth={1.75}
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-[13px] font-semibold uppercase tracking-widest text-zinc-700">
              LLM Pipeline
            </h2>
            <span
              className="text-[11px] text-zinc-700"
              data-testid="pipeline-status-label"
            >
              {!loaded
                ? "Loading…"
                : err
                  ? "Status unavailable"
                  : idle
                    ? "Idle"
                    : "Running"}
            </span>
          </div>

          {/* Active task line */}
          {active ? (
            <p
              className="mt-1.5 text-[14px] text-zinc-950"
              data-testid="pipeline-active-line"
            >
              <span className="font-medium">{STAGE_LABEL[active.stage] || active.stage}</span>
              <span className="text-zinc-700"> · </span>
              <span data-testid="pipeline-active-account">{active.account}</span>
              <span className="text-zinc-700"> · </span>
              <span
                className="font-mono text-[13px] text-amber-800"
                data-testid="pipeline-active-elapsed"
              >
                {fmtElapsed(liveElapsed)} elapsed
              </span>
            </p>
          ) : (
            <p
              className="mt-1.5 text-[14px] text-zinc-700"
              data-testid="pipeline-active-line"
            >
              {idle ? "No active task." : "—"}
            </p>
          )}

          {/* Queue */}
          {state.queue_depth > 0 && (
            <div className="mt-2" data-testid="pipeline-queue-block">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-zinc-700 mb-1">
                Queued ({state.queue_depth})
              </p>
              <ul className="text-[13px] text-zinc-900 space-y-0.5">
                {state.queue.map((q, i) => (
                  <li
                    key={`${q.account}-${q.queued_at}-${i}`}
                    className="flex items-center gap-2"
                    data-testid={`pipeline-queue-item-${i}`}
                  >
                    <span className="w-4 text-right text-[11px] text-zinc-500 font-mono">
                      {i + 1}.
                    </span>
                    <span className="font-medium text-zinc-950">{q.account}</span>
                    <span className="text-zinc-700">·</span>
                    <span className="text-zinc-700">{STAGE_LABEL[q.stage] || q.stage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Last completed */}
          {lastCompletedLine && (
            <p
              className="mt-2 text-[12px] text-zinc-700"
              data-testid="pipeline-last-completed"
            >
              {lastCompletedLine}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function AdminAccountsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [health, setHealth] = useState({});  // { account_id: {total_sources, dead_links, dead_link_pct} }
  // Beta admin onboarding — modal state for + Add account
  const [addModalOpen, setAddModalOpen] = useState(false);
  // v6.5.1 — Remove account: single-select row state + modal state. The
  // Remove button in the header is disabled until exactly one row is
  // selected, per spec.
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, sent: 0 });
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCounts, setWaitlistCounts] = useState({ total: 0, monthly: 0, annual: 0 });
  const [loading, setLoading] = useState(true);
  const [flippingId, setFlippingId] = useState(null);
  // Admin table chrome — "Show advanced columns" toggle reveals diagnostic
  // columns (ID, Last run, Last flipped) that are useful for debugging but
  // noisy day-to-day. Persists across sessions so the choice sticks.
  const ADVANCED_COLUMNS_KEY = "kalib.admin.showAdvancedColumns";
  const [showAdvancedColumns, setShowAdvancedColumns] = useState(() => {
    try {
      return localStorage.getItem(ADVANCED_COLUMNS_KEY) === "1";
    } catch { return false; }
  });
  const toggleAdvancedColumns = () => {
    setShowAdvancedColumns((prev) => {
      const next = !prev;
      try { localStorage.setItem(ADVANCED_COLUMNS_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  // Wave 3e — per-account-mode refresh state: "deutsche_bank:synthesis_only" etc.
  // Wave 7b (2026-04-25) — persisted to sessionStorage so a hard refresh
  // resumes polling instead of leaving the user blind to an in-flight job.
  const REFRESH_STATE_KEY = "kalib.admin.refreshing";
  const [refreshingKey, setRefreshingKeyState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(REFRESH_STATE_KEY);
      if (!raw) return null;
      const { key, startedAt } = JSON.parse(raw);
      // Drop if stale > 25 min (synthesis ceiling is ~20 min).
      if (!key || !startedAt || Date.now() - startedAt > 25 * 60 * 1000) {
        sessionStorage.removeItem(REFRESH_STATE_KEY);
        return null;
      }
      return key;
    } catch { return null; }
  });
  const setRefreshingKey = useCallback((key) => {
    setRefreshingKeyState(key);
    if (key) sessionStorage.setItem(REFRESH_STATE_KEY, JSON.stringify({ key, startedAt: Date.now() }));
    else sessionStorage.removeItem(REFRESH_STATE_KEY);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [accRes, notifRes, waitRes, healthRes] = await Promise.all([
        API.get("/accounts"),
        API.get("/admin/notifications"),
        API.get("/admin/waitlist"),
        API.get("/admin/accounts/health").catch(() => ({ data: { accounts: [] } })),
      ]);
      setAccounts(accRes.data.accounts || []);
      setNotifications(notifRes.data.notifications || []);
      setCounts(notifRes.data.counts || { total: 0, pending: 0, sent: 0 });
      setWaitlist(waitRes.data.waitlist || []);
      setWaitlistCounts(waitRes.data.counts || { total: 0, monthly: 0, annual: 0 });
      const healthMap = {};
      for (const h of healthRes.data.accounts || []) {
        healthMap[h.account_id] = h;
      }
      setHealth(healthMap);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Wave 7b — extracted polling loop so we can resume after hard refresh.
  // Polls /admin/accounts/health every 10s until the reveal's generated_at
  // bumps past the pre-refresh value (OR until 20 min elapses).
  //
  // 2026-05-22 fix — was previously comparing per-axis `revisions.stored`
  // strings (synthesis/matrix/calibration). Problem: those strings are
  // code-version constants — they only change when the underlying axis
  // code is bumped, NOT on every regen. So a Full Regen's matrix and
  // calibration stamps stay equal pre/post → completion check never
  // fired → button hung in "running" state until the 20-min timeout
  // even though chips had already flipped green. `reveal_generated_at`
  // is the actual per-run timestamp; it bumps on every successful regen
  // regardless of mode (synthesis_only, matrix_only, full).
  const pollUntilStored = useCallback(
    async (accountId, accountName, mode, beforeGeneratedAt, humanMode) => {
      for (let attempt = 0; attempt < 120; attempt++) {
        await new Promise((r) => setTimeout(r, 10000));
        let latest;
        try {
          const { data } = await API.get("/admin/accounts/health");
          latest = (data.accounts || []).find((h) => h.account_id === accountId);
          const healthMap = {};
          for (const h of data.accounts || []) healthMap[h.account_id] = h;
          setHealth(healthMap);
        } catch {
          continue;
        }
        const newGen = latest?.reveal_generated_at || null;
        // Completion: the reveal_generated_at has bumped past the
        // pre-refresh value (strict newer). null-safe: if there was no
        // pre-refresh value, any non-null new value counts as a bump.
        const bumped = newGen && (!beforeGeneratedAt || newGen > beforeGeneratedAt);
        if (bumped) {
          setRefreshingKey(null);
          toast.success(`${humanMode} refresh complete for ${accountName}.`);
          await refresh();
          return;
        }
      }
      setRefreshingKey(null);
      toast.error(
        `${humanMode} refresh for ${accountName} didn't complete within 20 minutes. ` +
        `Check backend logs and re-run if needed.`
      );
      await refresh();
    },
    [refresh, setRefreshingKey],
  );

  // Wave 7b — on mount, if sessionStorage shows a refresh in flight, resume
  // polling immediately. Uses current `reveal_generated_at` as the "before"
  // baseline — polling will fire the moment the next regen completes (a
  // hard refresh mid-regen captures a stale baseline; in the worst case
  // we get a duplicate success toast, never a stuck button).
  //
  // 2026-05-22 self-heal — if a regen already completed AFTER the
  // refresh-start timestamp (sessionStorage `startedAt`), the polling
  // would have caught it but we lost the resume. Detect this by
  // comparing reveal_generated_at against startedAt — if the reveal is
  // already newer than when we kicked off, the regen is done; clear
  // immediately instead of waiting for the next poll tick.
  useEffect(() => {
    if (!refreshingKey) return;
    const [accountId, mode] = refreshingKey.split(":");
    if (!accountId || !mode) return;
    if (!health[accountId]) return;
    const acct = accounts.find((a) => a.id === accountId);
    if (!acct) return;
    const labelByMode = { synthesis_only: "synthesis", matrix_only: "matrix", full: "full (harvest + synthesis)" };
    const humanMode = labelByMode[mode] || mode;
    const beforeGeneratedAt = health[accountId]?.reveal_generated_at || null;

    // Self-heal: read the original startedAt from sessionStorage. If
    // reveal_generated_at is already newer, the regen completed while
    // we weren't polling — clear and exit.
    try {
      const raw = sessionStorage.getItem(REFRESH_STATE_KEY);
      if (raw) {
        const { startedAt } = JSON.parse(raw);
        if (startedAt && beforeGeneratedAt) {
          const revealMs = new Date(beforeGeneratedAt).getTime();
          if (!isNaN(revealMs) && revealMs >= startedAt) {
            setRefreshingKey(null);
            toast.success(`${humanMode} refresh complete for ${acct.name}.`);
            return;
          }
        }
      }
    } catch { /* fall through to polling */ }

    pollUntilStored(accountId, acct.name, mode, beforeGeneratedAt, humanMode);
    // Run once when accounts + health both arrive. Polling owns its own lifecycle from there.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user === undefined) return;
    if (!user.is_admin) {
      toast.error("Admin access required");
      navigate("/intelligence");
      return;
    }
    refresh();
  }, [user, navigate, refresh]);

  const handleFlip = async (accountId, currentStatus) => {
    const nextStatus = currentStatus === "live" ? "soon" : "live";
    const confirmMsg = nextStatus === "live"
      ? `Flip this account LIVE? All followers will be notified.`
      : `Flip this account back to coming-soon? No notifications sent.`;
    if (!window.confirm(confirmMsg)) return;

    setFlippingId(accountId);
    try {
      const { data } = await API.post("/admin/accounts/flip", {
        account_id: accountId,
        status: nextStatus,
      });
      if (data.notifications_enqueued > 0) {
        toast.success(`Flipped to ${nextStatus}. ${data.notifications_enqueued} follower notification(s) queued.`);
      } else {
        toast.success(`Flipped to ${nextStatus}.`);
      }
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFlippingId(null);
    }
  };

  // Beta admin scheduling — persist a weekday selection (or "not_scheduled")
  // immediately on dropdown change. Toast on success. No separate Save button.
  const handleScheduleChange = async (accountId, newDay) => {
    const wireDay = newDay === "not_scheduled" ? null : newDay;
    try {
      await API.patch(`/admin/accounts/${accountId}/schedule`, {
        weekly_run_day: wireDay,
      });
      toast.success(wireDay ? `Weekly refresh updated · ${wireDay}` : "Weekly refresh cleared");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Weekly refresh TIME (hour-granularity, Europe/London). Persisted to
  // accounts.weekly_run_time and read by the per-account scheduler tick.
  const handleScheduleTimeChange = async (accountId, newTime) => {
    try {
      await API.patch(`/admin/accounts/${accountId}/schedule_time`, {
        weekly_run_time: newTime,
      });
      toast.success(`Refresh time updated · ${newTime} London`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Beta admin onboarding — fire a manual full pipeline run for a LIVE
  // account. Same code path as the v5.1 admin/generate endpoint.
  const handleGenerateNow = async (accountId, accountName) => {
    if (!window.confirm(`Trigger a full pipeline refresh for ${accountName} now? Takes 5-15 minutes.`)) return;
    try {
      await API.post("/admin/generate", {
        account_name: accountName,
        mode: "full",
        background: true,
      });
      toast.success(`Full refresh started for ${accountName}`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleExportWaitlistCSV = async () => {
    try {
      const res = await API.get("/admin/waitlist/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `kalib-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Waitlist CSV downloaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Wave 3e — fire a background admin generate (synthesis_only / matrix_only /
  // full). The backend returns 202 immediately; we poll /admin/accounts/health
  // every 20s until the stored revision for that axis matches the current
  // code-level value. Timeout after ~20 minutes.
  const handleRefresh = useCallback(
    async (accountId, accountName, mode) => {
      const refreshKey = `${accountId}:${mode}`;
      const labelByMode = {
        synthesis_only: "synthesis",
        matrix_only: "matrix",
        full: "full (harvest + synthesis)",
      };
      const humanMode = labelByMode[mode] || mode;
      if (refreshingKey) {
        toast.error(`A refresh is already running: ${refreshingKey}`);
        return;
      }
      if (!window.confirm(
        `Run ${humanMode} refresh for ${accountName}? ` +
        `This can take 5–15 minutes. You'll see the stale chip clear when it completes.`
      )) {
        return;
      }

      setRefreshingKey(refreshKey);
      toast.info(`${humanMode} refresh started for ${accountName}. Polling for completion…`);
      try {
        await API.post("/admin/generate", {
          account_name: accountName,
          mode,
          background: true,
        });
      } catch (err) {
        setRefreshingKey(null);
        toast.error(formatApiError(err));
        return;
      }

      // Capture the reveal_generated_at snapshot we want to see bump.
      // 2026-05-22 fix — was using per-axis revision strings (stale, only
      // change on code bumps). reveal_generated_at bumps on every run.
      const beforeGeneratedAt = health[accountId]?.reveal_generated_at || null;

      await pollUntilStored(accountId, accountName, mode, beforeGeneratedAt, humanMode);
    },
    [refreshingKey, health, refresh, pollUntilStored, setRefreshingKey],
  );

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading admin console…</p>
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-accounts-page">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block" data-testid="admin-accounts-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="admin-accounts-header-logo" />
            </Link>
            <BetaBadge />
            <span className="text-[11px] font-medium uppercase tracking-widest text-amber-700 border border-amber-700/30 px-1.5 py-0.5 leading-none" data-testid="admin-badge">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/intelligence" className="text-[13px] text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="admin-intelligence-link">Intelligence</Link>
            <Link to="/admin/account-requests" className="text-[13px] text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="admin-account-requests-link">Requests</Link>
            <Link to="/admin/login-activity" className="text-[13px] text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="admin-login-activity-link">Logins</Link>
            <Link to="/settings/profile" className="text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="admin-settings-link">
              <Settings className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} className="text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading">Account operations</h1>
            <p className="text-sm text-zinc-900 mt-1">Flip accounts live. Watch the notification queue.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/preview"
              className="inline-flex items-center px-3 py-2 border border-zinc-200 hover:bg-zinc-50 text-sm font-medium text-zinc-900 transition-colors"
              data-testid="admin-preview-link"
              title="Preview the current week's reveal as any vendor × role × seniority lens"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Preview lens
            </Link>
            <Link
              to="/admin/eval"
              className="inline-flex items-center px-3 py-2 border border-zinc-200 hover:bg-zinc-50 text-sm font-medium text-zinc-900 transition-colors"
              data-testid="admin-eval-link"
              title="Eval Harness — browse reveals, tag as gold / bad, score cards, export gold/bad sets"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Eval
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v6.5.2`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 border border-amber-700 hover:bg-amber-50 text-sm font-medium text-amber-800 transition-colors"
              data-testid="admin-prompt-arch-v652-link"
              title="Download current prompt architecture (v6.5.2 omnibus). Full v6.5 content preserved + Beta Pre-flight doctrine stack: SIGNAL_DISCIPLINE (P1·1), VOICE_DOCTRINE + voice_validator (P1·2), DELIVERY_MOTION_FRAME (P1·3, conditional), CARD_BODY_STRUCTURE + body_structure_validator (P1·4), I_TIER_BRIDGE (P2·1), NAMED_STAKEHOLDER (P2·2), MES_RULES (P2·3), HARVEST_SOURCE_GATE (P3·1). Plus WORKED_EXAMPLE_1/2 few-shots, asyncio.wait_for() LLM timeouts, Briefa→Kalib rebrand, Landing Page refresh, Intake/Settings simplification, Admin weekly_run_time picker + advanced-columns toggle. Supersedes v6.5."
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Architecture v6.5.2
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prd`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 border border-zinc-300 hover:bg-zinc-50 text-sm font-medium text-zinc-900 transition-colors"
              data-testid="admin-prd-link"
              title="Download PRD.md — canonical product spec: what Kalib is, what it does, how it does it now. Single-screen reference for any agent or operator. Updated when the spec itself changes (not on every feature ship)."
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              PRD
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/changelog`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 border border-zinc-300 hover:bg-zinc-50 text-sm font-medium text-zinc-900 transition-colors"
              data-testid="admin-changelog-link"
              title="Download CHANGELOG.md — rolling delivery log, newest first. What shipped, when, with what effect. Preserves the full historical Drop/Wave/Chunk record."
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Changelog
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-none border-zinc-200 hover:bg-zinc-50 text-sm font-medium px-3 py-2"
                  data-testid="admin-prompt-arch-legacy-trigger"
                  title="Older prompt architecture versions (legacy)"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  Legacy
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-zinc-200 min-w-[240px]">
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v65-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v6.5`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v6.5 — Calibration overhaul omnibus, Chunks 3.8 → 3.12 (2026-05-05). Superseded by v6.5.2."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v6.5 — Calibration overhaul
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v6-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v6`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v6.0 — Chunk 2 + Chunk 3 omnibus (2026-05-03). Superseded by v6.5."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v6.0 — Chunk 2 + 3 omnibus
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v52-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v5.2`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v5.2 — Reveal Setup intake redesign (2026-04-27). Superseded by v6.0."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v5.2 — Reveal Setup
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v51-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v5.1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v5.1 — post-v5.1 omnibus + admin scheduling (2026-04-26). Superseded by v5.2."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v5.1 — Admin scheduling
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v5-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v5`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v5 — Omnibus Edition (wave9 / Chunk 4.5). Superseded by v5.1."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v5 — Omnibus
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-v4-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v4`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v4 — superseded by v5."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v4
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="admin-prompt-arch-link">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/prompt-architecture-v3`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                    title="v3 — earliest published architecture."
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                    v3
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={refresh}
              variant="outline"
              className="rounded-none border-zinc-200 hover:bg-zinc-50 text-sm font-medium"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Chunk 4.5 — LLM pipeline health (top of page, above Accounts) */}
        <PipelineHealthWidget
          onFullRunComplete={(lc) => {
            // Source counts + dead-link percentages only change after a `full`
            // run (harvest + synthesis). Auto-refresh the Accounts table to
            // pick up the new numbers without admin intervention.
            refresh();
            toast.success(`Accounts refreshed — ${lc.account} ${lc.stage} ${lc.status}`);
          }}
        />

        {/* Accounts table */}
        <section className="bg-white border border-zinc-200 mb-8" data-testid="admin-accounts-section">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-zinc-950 font-heading">Accounts</h2>
            <div className="flex items-center gap-3">
              <label
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-zinc-700 cursor-pointer select-none"
                data-testid="admin-advanced-columns-label"
                title="Show diagnostic columns (ID, Last run, Last flipped) — useful when debugging."
              >
                <input
                  type="checkbox"
                  checked={showAdvancedColumns}
                  onChange={toggleAdvancedColumns}
                  className="accent-amber-700 cursor-pointer"
                  data-testid="admin-advanced-columns-toggle"
                />
                Show advanced columns
              </label>
              <button
                type="button"
                onClick={() => setAddModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-800 hover:text-amber-900 border border-amber-700/30 hover:border-amber-700 px-2.5 py-1 transition-colors"
                data-testid="admin-add-account-btn"
                title="Onboard a new account from the Admin UI — no code edit required."
              >
                + Add account
              </button>
              <button
                type="button"
                onClick={() => { if (selectedAccountId) setRemoveModalOpen(true); }}
                disabled={!selectedAccountId}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-red-700 hover:text-red-800 border border-red-700/30 hover:border-red-700 px-2.5 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-700 disabled:hover:border-red-700/30"
                data-testid="admin-remove-account-btn"
                title={selectedAccountId
                  ? `Archive the selected account (soft remove — preserves historical data).`
                  : "Select an account in the table first to enable removal."}
              >
                Remove account
              </button>
              <button
                type="button"
                onClick={() => { refresh(); toast.success("Accounts refreshed"); }}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-zinc-700 hover:text-amber-800 transition-colors px-2 py-1 -mr-2"
                data-testid="refresh-accounts-table-btn"
                title="Re-fetch source counts, dead-link percentages, and last-flipped timestamps"
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} />
                Refresh
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-900">
                <th className="px-3 py-3 font-medium w-10">
                  <span className="sr-only">Select</span>
                </th>
                <th className="px-4 py-3 font-medium">Account</th>
                {showAdvancedColumns && <th className="px-4 py-3 font-medium">ID</th>}
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Weekly refresh</th>
                <th className="px-4 py-3 font-medium">Sources</th>
                {showAdvancedColumns && <th className="px-4 py-3 font-medium">Last run</th>}
                {showAdvancedColumns && <th className="px-4 py-3 font-medium">Last flipped</th>}
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const isLive = a.status === "live";
                const h = health[a.id] || {};
                const scheduleValue = h.weekly_run_day || a.weekly_run_day || "not_scheduled";
                const scheduleTime = h.weekly_run_time || a.weekly_run_time || "07:00";
                const lastRunAt = h.last_scheduled_run_at;
                const lastRunStatus = h.last_scheduled_run_status;
                return (
                  <tr
                    key={a.id}
                    className={`border-b border-zinc-100 last:border-0 ${selectedAccountId === a.id ? "bg-red-50/40" : ""}`}
                    data-testid={`admin-account-row-${a.id}`}
                  >
                    <td className="px-3 py-4">
                      <input
                        type="radio"
                        name="admin-account-select"
                        value={a.id}
                        checked={selectedAccountId === a.id}
                        onChange={() => setSelectedAccountId(a.id)}
                        className="accent-red-600 cursor-pointer"
                        data-testid={`admin-account-select-${a.id}`}
                        aria-label={`Select ${a.name} for removal`}
                        title={`Select ${a.name} for removal`}
                      />
                    </td>
                    <td className="px-4 py-4 font-medium text-zinc-950">{a.name}</td>
                    {showAdvancedColumns && (
                      <td className="px-4 py-4 text-zinc-900 font-mono text-[12px]">{a.id}</td>
                    )}
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium uppercase tracking-widest ${
                          isLive
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-zinc-50 text-zinc-900 border border-zinc-200"
                        }`}
                        data-testid={`admin-account-status-${a.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-emerald-500" : "bg-zinc-400"}`} />
                        {isLive ? "Live" : "Coming soon"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={scheduleValue}
                        onChange={(e) => handleScheduleChange(a.id, e.target.value)}
                        className="block w-full px-2 py-1.5 border border-zinc-200 bg-white text-zinc-900 text-[12px] focus:outline-none focus:border-amber-700"
                        data-testid={`admin-account-schedule-${a.id}`}
                      >
                        <option value="not_scheduled">Not scheduled</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                      <select
                        value={scheduleTime}
                        onChange={(e) => handleScheduleTimeChange(a.id, e.target.value)}
                        disabled={scheduleValue === "not_scheduled"}
                        className="block w-full px-2 py-1.5 mt-1 border border-zinc-200 bg-white text-zinc-900 text-[12px] focus:outline-none focus:border-amber-700 disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed"
                        data-testid={`admin-account-schedule-time-${a.id}`}
                        aria-label={`Refresh hour for ${a.name}`}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hh = String(i).padStart(2, "0");
                          return (
                            <option key={hh} value={`${hh}:00`}>{hh}:00</option>
                          );
                        })}
                      </select>
                      {scheduleValue !== "not_scheduled" && (
                        <p className="mt-1 text-[10px] text-zinc-500" data-testid={`admin-account-schedule-hint-${a.id}`}>
                          Runs weekly · {scheduleTime} London
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {(() => {
                        if (!isLive) {
                          return <span className="text-zinc-400 text-[12px]" data-testid={`admin-account-sources-${a.id}`}>—</span>;
                        }
                        if (!h || h.total_sources === 0) {
                          return <span className="text-zinc-400 text-[12px]" data-testid={`admin-account-sources-${a.id}`}>No reveal</span>;
                        }
                        const dead = h.dead_links || 0;
                        const total = h.total_sources || 0;
                        const pct = h.dead_link_pct || 0;
                        const accent = dead === 0 ? "text-emerald-700" : pct >= 40 ? "text-red-700" : "text-amber-700";
                        return (
                          <span className="inline-flex items-center gap-2 text-[12px]" data-testid={`admin-account-sources-${a.id}`}>
                            <span className="text-zinc-900 font-mono">{total}</span>
                            <span className={`font-medium ${accent}`}>{dead} dead{total > 0 ? ` · ${pct}%` : ""}</span>
                          </span>
                        );
                      })()}
                    </td>
                    {showAdvancedColumns && (
                      <td className="px-4 py-4 text-[12px]" data-testid={`admin-account-last-run-${a.id}`}>
                        {lastRunAt ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-zinc-900">{formatTimestamp(lastRunAt)}</span>
                            {lastRunStatus && (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium ${
                                  lastRunStatus === "success"
                                    ? "text-emerald-700"
                                    : lastRunStatus === "failed"
                                    ? "text-red-700"
                                    : lastRunStatus === "running"
                                    ? "text-amber-700"
                                    : "text-zinc-500"
                                }`}
                                data-testid={`admin-account-last-run-status-${a.id}`}
                              >
                                · {lastRunStatus}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    )}
                    {showAdvancedColumns && (
                      <td className="px-4 py-4 text-zinc-900 text-[12px]">{formatTimestamp(a.flipped_at)}</td>
                    )}
                    <td className="px-4 py-4 text-right">
                      <div className="inline-flex flex-col items-end gap-1.5">
                        <Button
                          onClick={() => handleFlip(a.id, a.status)}
                          disabled={flippingId === a.id}
                          variant={isLive ? "outline" : "default"}
                          className={`rounded-none text-xs font-medium px-3 py-1.5 h-auto ${
                            isLive
                              ? "border-zinc-300 hover:bg-zinc-50 text-zinc-700"
                              : "bg-amber-700 text-white hover:bg-amber-800"
                          }`}
                          data-testid={`admin-account-flip-${a.id}`}
                        >
                          {flippingId === a.id ? "Flipping..." : isLive ? "Flip to coming-soon" : "Flip to live"}
                        </Button>
                        {isLive && (
                          <button
                            type="button"
                            onClick={() => handleGenerateNow(a.id, a.name)}
                            className="text-[11px] font-medium text-amber-700 hover:text-amber-800 underline-offset-2 hover:underline"
                            data-testid={`admin-account-generate-now-${a.id}`}
                            title="Run the full pipeline (harvest + synthesis) now. Same code path as the scheduled refresh."
                          >
                            Generate now
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {/* Wave 3e — Reveal freshness (revision-aware) */}
        <section className="bg-white border border-zinc-200 mb-8" data-testid="admin-freshness-section">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-zinc-950 font-heading">Reveal freshness</h2>
              <p className="text-[12px] text-zinc-900 mt-1">
                Compares each live account's stored reveal against the current code-level revisions (<span className="font-mono">synthesis · matrix · calibration</span>). Use per-axis refresh to only regenerate what's stale.
              </p>
            </div>
          </div>
          {/* Wave 7b — global in-flight banner. Tells the admin which job is
               running and that the dashboard is auto-polling for completion.
               Survives hard refresh thanks to sessionStorage persistence. */}
          {refreshingKey && (
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-3" data-testid="admin-inflight-banner">
              <RefreshCw className="w-4 h-4 text-amber-700 animate-spin" strokeWidth={1.5} />
              <div className="text-[13px] text-amber-900">
                <span className="font-medium">In progress:</span>{" "}
                {(() => {
                  const [accountId, mode] = refreshingKey.split(":");
                  const acct = accounts.find((x) => x.id === accountId);
                  const labelByMode = { synthesis_only: "Synthesis", matrix_only: "Matrix", full: "Full regenerate" };
                  return `${labelByMode[mode] || mode} for ${acct?.name || accountId}`;
                })()}
                <span className="text-amber-800 ml-1">— polling every 10s. You can keep using the rest of the dashboard; the chips below will go green when it finishes.</span>
              </div>
            </div>
          )}
          {accounts.filter((a) => a.status === "live").length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-zinc-900" data-testid="admin-freshness-empty">
              No live accounts yet.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {accounts.filter((a) => a.status === "live").map((a) => {
                const h = health[a.id];
                const revs = h?.revisions || null;
                const stored = revs?.stored || {};
                const current = revs?.current || {};
                const mismatched = new Set(revs?.mismatched_axes || []);
                const isStale = revs?.is_stale;
                const axes = ["synthesis", "matrix", "calibration"];
                const inflight = (mode) => refreshingKey === `${a.id}:${mode}`;
                const anyInflight = refreshingKey?.startsWith(`${a.id}:`);
                // Wave 7b — disable per-axis refresh when that axis is already fresh.
                // Synthesis-only refresh affects synthesis + calibration axes.
                // Matrix-only refresh affects only the matrix axis.
                const matrixFresh = revs && !mismatched.has("matrix");
                const synthFresh = revs && !mismatched.has("synthesis") && !mismatched.has("calibration");
                const allFresh = revs && !isStale;

                return (
                  <div key={a.id} className="px-6 py-5" data-testid={`admin-freshness-row-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-zinc-950">{a.name}</span>
                          {revs && (
                            isStale ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-widest bg-red-50 text-red-700 border border-red-200" data-testid={`admin-freshness-stale-${a.id}`}>
                                Stale
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`admin-freshness-fresh-${a.id}`}>
                                Fresh
                              </span>
                            )
                          )}
                          {!revs && (
                            <span className="text-[12px] text-zinc-900">No reveal stored</span>
                          )}
                          {/* Chunk 1 / Task 6 — QA status badge */}
                          {h?.qa_status && h.qa_status !== "unknown" && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-widest border ${
                                h.qa_status === "pass"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : h.qa_status === "warn"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-red-50 text-red-700 border-red-200"
                              }`}
                              data-testid={`admin-qa-status-${a.id}`}
                              title="Synthesis QA status for this reveal"
                            >
                              QA {h.qa_status}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-700 mt-1">
                          Generated: <span className="font-mono">{formatTimestamp(h?.reveal_generated_at)}</span>
                          {h?.generation_cycle && <> · Cycle: <span className="font-mono">{h.generation_cycle}</span></>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => handleRefresh(a.id, a.name, "matrix_only")}
                          disabled={!!anyInflight || !revs || matrixFresh}
                          variant="outline"
                          className="rounded-none text-xs font-medium px-3 py-1.5 h-auto border-zinc-300 hover:bg-zinc-50 text-zinc-900 disabled:opacity-40"
                          data-testid={`admin-refresh-matrix-${a.id}`}
                          title={matrixFresh ? "Matrix is up to date — no refresh needed" : "Regenerate only the implication matrix layer"}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1.5 ${inflight("matrix_only") ? "animate-spin" : ""}`} strokeWidth={1.5} />
                          {inflight("matrix_only") ? "Refreshing matrix…" : matrixFresh ? "Matrix up to date" : "Refresh matrix"}
                        </Button>
                        <Button
                          onClick={() => handleRefresh(a.id, a.name, "synthesis_only")}
                          disabled={!!anyInflight || !revs || synthFresh}
                          variant="outline"
                          className="rounded-none text-xs font-medium px-3 py-1.5 h-auto border-amber-700 hover:bg-amber-50 text-amber-800 disabled:opacity-40"
                          data-testid={`admin-refresh-synthesis-${a.id}`}
                          title={synthFresh ? "Synthesis is up to date — no refresh needed" : "Regenerate the full synthesis pipeline against the existing harvest"}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1.5 ${inflight("synthesis_only") ? "animate-spin" : ""}`} strokeWidth={1.5} />
                          {inflight("synthesis_only") ? "Refreshing synthesis…" : synthFresh ? "Synthesis up to date" : "Refresh synthesis"}
                        </Button>
                        <Button
                          onClick={() => handleRefresh(a.id, a.name, "full")}
                          disabled={!!anyInflight}
                          className="rounded-none text-xs font-medium px-3 py-1.5 h-auto bg-zinc-900 hover:bg-zinc-950 text-white disabled:opacity-40"
                          data-testid={`admin-refresh-full-${a.id}`}
                          title={allFresh ? "Pull a fresh harvest and regenerate (re-fetches signals from the wire)" : "Fresh harvest + synthesis"}
                        >
                          <Sparkles className={`w-3 h-3 mr-1.5 ${inflight("full") ? "animate-spin" : ""}`} strokeWidth={1.5} />
                          {inflight("full") ? "Full regen running…" : "Full regenerate"}
                        </Button>
                      </div>
                    </div>

                    {revs && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                        {axes.map((axis) => {
                          const axisStale = mismatched.has(axis);
                          const storedVal = stored[axis] || "—";
                          const currentVal = current[axis] || "—";
                          return (
                            <div
                              key={axis}
                              className={`border p-2.5 text-[11px] ${
                                axisStale ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"
                              }`}
                              data-testid={`admin-freshness-axis-${a.id}-${axis}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium uppercase tracking-widest text-zinc-900">{axis}</span>
                                <span className={`font-medium ${axisStale ? "text-red-700" : "text-emerald-700"}`}>
                                  {axisStale ? "Stale" : "Fresh"}
                                </span>
                              </div>
                              <div className="text-zinc-900">
                                Stored: <span className="font-mono">{storedVal}</span>
                              </div>
                              <div className="text-zinc-900">
                                Current: <span className="font-mono">{currentVal}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Chunk 2 final closure — multi-pass harvest health tile.
                         One dot per pass (account_anchor / peer_bank / vendor_partner /
                         regulatory_sector). Green = ok. Amber = retried-then-ok. Red = failed/timeout.
                         Tooltip carries duration + signal count. */}
                    {h?.multipass_audit && (
                      <div
                        className={`mt-3 border p-2.5 text-[11px] ${
                          h.multipass_audit.harvest_degraded
                            ? "border-amber-300 bg-amber-50"
                            : "border-zinc-200 bg-zinc-50"
                        }`}
                        data-testid={`admin-multipass-tile-${a.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium uppercase tracking-widest text-zinc-900">
                            Multi-pass harvest
                          </span>
                          <span className="text-zinc-700 flex items-center gap-2 flex-wrap">
                            <span>
                              wall <span className="font-mono">{Math.round((h.multipass_audit.wall_clock_ms || 0) / 100) / 10}s</span>
                              <span className="mx-1">·</span>
                              dedupe <span className="font-mono">{h.multipass_audit.total_pre_dedupe}→{h.multipass_audit.total_post_dedupe}</span>
                            </span>
                            {h.url_trust_audit && h.url_trust_audit.total > 0 && (() => {
                              // Chunk 3 / PRE-TASK — trust-list audit pill
                              // with hover-revealed "cites by domain" breakdown.
                              const ua = h.url_trust_audit;
                              const trustedPct = ua.total ? (ua.domain_trusted_count / ua.total) : 0;
                              const pathological = ua.domain_trusted_count === 0 || trustedPct > 0.8;
                              const domains = ua.top_domains || [];
                              return (
                                <span
                                  className="relative group inline-block"
                                  data-testid={`admin-trust-pill-${a.id}`}
                                >
                                  <span
                                    className={`inline-block px-1.5 py-0.5 border uppercase tracking-widest font-mono cursor-help ${
                                      pathological
                                        ? "bg-amber-50 text-amber-800 border-amber-300"
                                        : "bg-zinc-100 text-zinc-700 border-zinc-300"
                                    }`}
                                  >
                                    trust {ua.domain_trusted_count}/{ua.total}
                                    <span className="mx-1 opacity-60">·</span>
                                    bypass {ua.url_verification_bypassed_count}
                                  </span>
                                  {/* Hover-revealed breakdown — shows top 5
                                      domains with signal counts and trust
                                      stamps. Distinguishes diverse harvest
                                      vs. echo-chamber PR feed at a glance. */}
                                  {domains.length > 0 && (
                                    <span
                                      className="hidden group-hover:flex absolute right-0 top-full mt-1 z-20 flex-col bg-white border border-zinc-300 shadow-lg p-3 min-w-[280px] text-[11px] normal-case tracking-normal"
                                      data-testid={`admin-trust-breakdown-${a.id}`}
                                    >
                                      <span className="font-medium uppercase tracking-widest text-zinc-900 pb-2 mb-2 border-b border-zinc-200">
                                        Cites by domain · top {domains.length}
                                      </span>
                                      {domains.map((d) => (
                                        <span
                                          key={d.domain}
                                          className="flex items-center justify-between gap-3 py-0.5"
                                        >
                                          <span className="flex items-center gap-1.5 truncate text-zinc-900">
                                            <span
                                              className={`inline-block w-1.5 h-1.5 rounded-full ${
                                                d.trusted ? "bg-emerald-500" : "bg-zinc-300"
                                              }`}
                                            />
                                            <span className="font-mono truncate">{d.domain}</span>
                                          </span>
                                          <span className="font-mono text-zinc-700 shrink-0">
                                            {d.count}
                                          </span>
                                        </span>
                                      ))}
                                      <span className="pt-2 mt-2 border-t border-zinc-200 text-zinc-500 italic">
                                        Green dot = trusted (account-primary, top-tier press, regulator, market data).
                                      </span>
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            {h.multipass_audit.harvest_degraded && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-widest">
                                degraded
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(h.multipass_audit.passes || []).map((p) => {
                            const isOk = p.status === "ok";
                            const isFail = p.status === "failed" || p.status === "timeout";
                            const colour = isFail
                              ? "bg-red-500"
                              : p.retried
                              ? "bg-amber-500"
                              : isOk
                              ? "bg-emerald-500"
                              : "bg-zinc-400";
                            return (
                              <div
                                key={p.name}
                                className="flex items-center gap-2 bg-white border border-zinc-200 px-2 py-1.5"
                                data-testid={`admin-multipass-pass-${a.id}-${p.name}`}
                                title={`${p.name} · status=${p.status}${p.retried ? " (retried)" : ""} · ${Math.round(p.duration_ms / 100) / 10}s · ${p.signals_count} signals`}
                              >
                                <span className={`inline-block w-2 h-2 rounded-full ${colour}`} />
                                <span className="font-medium text-zinc-900 truncate">
                                  {p.name.replace("_", " ")}
                                </span>
                                <span className="ml-auto font-mono text-zinc-700">
                                  n={p.signals_count}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Chunk 3 / TASK 2 — lens-validator pill row.
                         Renders three headline chips (contamination, altitude,
                         readability) + a rewrite-triggered badge when any
                         TASK 3 flag is set. When the validator has not yet
                         run on this account's current reveal (validator is
                         new — pre-existing reveals show `present=False`),
                         the row renders a single grey "lens — not yet run"
                         chip so the operator knows to regen. */}
                    {h.lens_validation && (() => {
                      const lv = h.lens_validation;
                      if (!lv.present) {
                        return (
                          <div
                            className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center gap-2"
                            data-testid={`admin-lens-pill-absent-${a.id}`}
                          >
                            <span className="font-medium uppercase tracking-widest text-zinc-700">
                              Lens validator
                            </span>
                            <span className="text-zinc-500 italic">
                              not yet run — regenerate to populate
                            </span>
                          </div>
                        );
                      }
                      // Chip classification.
                      // Contamination: 0-30 green, 30-40 amber, >40 red.
                      // Altitude:      0-10 green, 10-20 amber, >20 red.
                      // Readability:   >=0.6 green, 0.4-0.6 amber, <0.4 red.
                      const contamColor =
                        lv.contamination_pct > 40 ? "red" :
                        lv.contamination_pct > 30 ? "amber" : "emerald";
                      const altColor =
                        lv.altitude_pct > 20 ? "red" :
                        lv.altitude_pct > 10 ? "amber" : "emerald";
                      const readColor =
                        lv.avg_readability < 0.4 ? "red" :
                        lv.avg_readability < 0.6 ? "amber" : "emerald";
                      const chipCls = (c) => `px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                        c === "red"
                          ? "bg-red-50 text-red-800 border-red-300"
                          : c === "amber"
                          ? "bg-amber-50 text-amber-800 border-amber-300"
                          : "bg-emerald-50 text-emerald-800 border-emerald-300"
                      }`;
                      const warnings = lv.warnings || [];
                      return (
                        <div
                          className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
                          data-testid={`admin-lens-pill-${a.id}`}
                        >
                          <span className="font-medium uppercase tracking-widest text-zinc-900">
                            Lens validator
                          </span>
                          <span className="flex items-center gap-2 flex-wrap">
                            <span
                              className={chipCls(contamColor)}
                              title={`Contamination — % of actions whose primary_company_type mismatches the user's intake lens. 0-30 healthy, 30-40 warn, >40 triggers TASK 3 rewrite.`}
                              data-testid={`admin-lens-contam-${a.id}`}
                            >
                              contam {lv.contamination_pct.toFixed(0)}%
                            </span>
                            <span
                              className={chipCls(altColor)}
                              title={`Altitude misalignment — % of actions using wrong-tier vocabulary (IC mentioning 'brief the board', Manager 'jira/ticket', Leader 'pull request').`}
                              data-testid={`admin-lens-alt-${a.id}`}
                            >
                              altitude {lv.altitude_pct.toFixed(0)}%
                            </span>
                            <span
                              className={chipCls(readColor)}
                              title={`Signal readability — avg per-card punchiness score. >=0.6 healthy, 0.4-0.6 warn, <0.4 triggers TASK 3 sentence rewrite.`}
                              data-testid={`admin-lens-read-${a.id}`}
                            >
                              read {lv.avg_readability.toFixed(2)}
                            </span>
                            {(lv.needs_lens_rewrite || lv.needs_readability_rewrite) && (
                              <span
                                className="px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-widest font-mono"
                                title={
                                  (lv.needs_lens_rewrite ? "contamination > 40% — lens rewrite queued for TASK 3" : "") +
                                  (lv.needs_lens_rewrite && lv.needs_readability_rewrite ? "; " : "") +
                                  (lv.needs_readability_rewrite ? "avg readability < 0.4 — sentence rewrite queued for TASK 3" : "")
                                }
                                data-testid={`admin-lens-rewrite-${a.id}`}
                              >
                                rewrite queued
                              </span>
                            )}
                          </span>
                          {warnings.length > 0 && (
                            <span
                              className="basis-full text-zinc-700 italic"
                              data-testid={`admin-lens-warnings-${a.id}`}
                            >
                              {warnings.join(" · ")}
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    {/* Chunk 3 / TASK 3 — lens rewrite audit pill. Shows
                         how many actions were rewritten per (tier, lens)
                         where contamination>40%. Hidden when not present
                         (legacy reveals); muted when no rewrites were
                         needed; amber when calls fired. */}
                    {h.lens_rewrite && h.lens_rewrite.present && (
                      <div
                        className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`admin-lens-rewrite-pill-${a.id}`}
                      >
                        <span className="font-medium uppercase tracking-widest text-zinc-900">
                          Lens variants
                        </span>
                        <span className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                              (h.lens_rewrite.total_rewritten || 0) > 0
                                ? "bg-amber-50 text-amber-800 border-amber-300"
                                : "bg-zinc-100 text-zinc-700 border-zinc-300"
                            }`}
                            title={
                              (h.lens_rewrite.total_rewritten || 0) > 0
                                ? `${h.lens_rewrite.total_rewritten} action(s) rewritten across ${(h.lens_rewrite.calls || []).length} (tier × lens) cell(s) — pre-computed at synthesis, served instantly per user lens.`
                                : "No lens above the 40% contamination threshold — no rewrites needed."
                            }
                            data-testid={`admin-lens-rewrite-count-${a.id}`}
                          >
                            rewrote {h.lens_rewrite.total_rewritten || 0}
                          </span>
                          <span
                            className="px-1.5 py-0.5 border bg-white text-zinc-700 border-zinc-300 uppercase tracking-widest font-mono"
                            data-testid={`admin-lens-rewrite-calls-${a.id}`}
                            title={
                              (h.lens_rewrite.calls || [])
                                .map(c => `${c.tier}/${c.lens}: ${c.rewritten}/${c.contaminated}`)
                                .join(" · ") || "No calls fired."
                            }
                          >
                            calls {(h.lens_rewrite.calls || []).length}
                          </span>
                        </span>
                      </div>
                    )}

                    {/* Chunk 3 / TASK 5 — hallucination guardrail pill.
                         Surfaces unverified named persons (proper nouns
                         in actions that don't appear in the U-tier signal
                         corpus). Red when count>0. */}
                    {h.named_person_audit && h.named_person_audit.present && (
                      <div
                        className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`admin-named-person-pill-${a.id}`}
                      >
                        <span className="font-medium uppercase tracking-widest text-zinc-900">
                          Named persons
                        </span>
                        <span className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                              (h.named_person_audit.unverified_count || 0) > 0
                                ? "bg-red-50 text-red-800 border-red-300"
                                : "bg-emerald-50 text-emerald-800 border-emerald-300"
                            }`}
                            title={
                              (h.named_person_audit.unverified_count || 0) > 0
                                ? `${h.named_person_audit.unverified_count} candidate name(s) NOT found in the U-tier signal corpus — likely hallucinations. Examples: ${(h.named_person_audit.examples || []).slice(0,3).map(e => (e.names || []).join(", ")).join(" | ")}`
                                : `${h.named_person_audit.verified_count} named person(s) verified against the signal corpus.`
                            }
                            data-testid={`admin-named-person-unverified-${a.id}`}
                          >
                            unverified {h.named_person_audit.unverified_count || 0}
                          </span>
                          <span
                            className="px-1.5 py-0.5 border bg-white text-zinc-700 border-zinc-300 uppercase tracking-widest font-mono"
                            title="Total proper-noun candidates that DID appear in the U-tier signal corpus."
                            data-testid={`admin-named-person-verified-${a.id}`}
                          >
                            verified {h.named_person_audit.verified_count || 0}
                          </span>
                        </span>
                        {(h.named_person_audit.examples || []).length > 0 && (
                          <span
                            className="basis-full text-zinc-700 italic"
                            data-testid={`admin-named-person-examples-${a.id}`}
                          >
                            {(h.named_person_audit.examples || []).slice(0, 3).map((e, i) =>
                              `${e.tier}: ${(e.names || []).join(", ")}${e.action_title ? ` — ${e.action_title.slice(0, 50)}…` : ""}`
                            ).join(" · ")}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Chunk 3 / TASK 5 — citation link audit pill.
                         Counts of verified / trusted-domain-only / dead
                         citation links. Renderer already downgrades dead
                         links to plain outlet+date; this pill is admin-only.
                         Chunk 3.6 — ARCHIVE branch added. Dead URLs with an
                         archive.org snapshot now render as archive links. */}
                    {h.citation_audit && h.citation_audit.present && (
                      <div
                        className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`admin-citation-audit-pill-${a.id}`}
                      >
                        <span className="font-medium uppercase tracking-widest text-zinc-900">
                          Citation links
                        </span>
                        <span className="flex items-center gap-2 flex-wrap">
                          <span
                            className="px-1.5 py-0.5 border bg-emerald-50 text-emerald-800 border-emerald-300 uppercase tracking-widest font-mono"
                            title="Sources where url_verified=true (HEAD/GET succeeded post-harvest)."
                            data-testid={`admin-citation-verified-${a.id}`}
                          >
                            verified {h.citation_audit.verified_count || 0}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                              (h.citation_audit.archive_count || 0) > 0
                                ? "bg-sky-50 text-sky-800 border-sky-300"
                                : "bg-zinc-100 text-zinc-700 border-zinc-300"
                            }`}
                            title="Chunk 3.6 — sources where the original URL failed HEAD but archive.org has a snapshot. Citation chip links to the archive with a subtle '(archived)' suffix."
                            data-testid={`admin-citation-archive-${a.id}`}
                          >
                            archive {h.citation_audit.archive_count || 0}
                          </span>
                          <span
                            className="px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300 uppercase tracking-widest font-mono"
                            title="Sources without HEAD-verification but on the trusted-domain bypass list."
                            data-testid={`admin-citation-trusted-${a.id}`}
                          >
                            trusted {h.citation_audit.trusted_only_count || 0}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                              (h.citation_audit.dead_count || 0) > 0
                                ? "bg-red-50 text-red-800 border-red-300"
                                : "bg-zinc-100 text-zinc-700 border-zinc-300"
                            }`}
                            title="Sources with no successful HEAD verification AND not on the trusted-domain list AND no archive.org snapshot. Renderer downgrades these to outlet+date plain text."
                            data-testid={`admin-citation-dead-${a.id}`}
                          >
                            dead {h.citation_audit.dead_count || 0}
                          </span>
                        </span>
                      </div>
                    )}

                    {/* Chunk 3 / TASK 5 — tier differentiation score.
                         Pairwise IC/Manager/Leader title similarity.
                         1.0 = totally distinct; <0.4 = identical-ish. */}
                    {h.tier_differentiation && h.tier_differentiation.present && (
                      <div
                        className="mt-2 border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`admin-tier-diff-pill-${a.id}`}
                      >
                        <span className="font-medium uppercase tracking-widest text-zinc-900">
                          Tier differentiation
                        </span>
                        <span
                          className={`px-1.5 py-0.5 border uppercase tracking-widest font-mono ${
                            (h.tier_differentiation.score || 0) >= 0.6
                              ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                              : (h.tier_differentiation.score || 0) >= 0.4
                              ? "bg-amber-50 text-amber-800 border-amber-300"
                              : "bg-red-50 text-red-800 border-red-300"
                          }`}
                          title="1.0 = IC/Manager/Leader titles totally distinct. <0.4 = the three tiers are saying the same thing — Manager+Leader cards collapse onto IC vocabulary."
                          data-testid={`admin-tier-diff-score-${a.id}`}
                        >
                          score {h.tier_differentiation.score.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Notifications queue */}
        <section className="bg-white border border-zinc-200" data-testid="admin-notifications-section">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-zinc-950 font-heading">Notifications queue</h2>
            <div className="flex items-center gap-4 text-[12px]" data-testid="admin-notif-counts">
              <span className="text-zinc-900">Total: <span className="font-medium text-zinc-950">{counts.total}</span></span>
              <span className="text-zinc-900">Pending: <span className="font-medium text-amber-700">{counts.pending}</span></span>
              <span className="text-zinc-900">Sent: <span className="font-medium text-emerald-700">{counts.sent}</span></span>
            </div>
          </div>
          {notifications.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-zinc-900" data-testid="admin-notif-empty">
              No notifications yet. Flip an account from coming-soon to live to enqueue follower alerts.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200">
                <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-900">
                  <th className="px-6 py-3 font-medium">Recipient</th>
                  <th className="px-6 py-3 font-medium">Kind</th>
                  <th className="px-6 py-3 font-medium">Account</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Queued at</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((n, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-0" data-testid={`admin-notif-row-${i}`}>
                    <td className="px-6 py-3 text-zinc-950">
                      {n.email}
                      {n.first_name && <span className="text-zinc-900"> · {n.first_name}</span>}
                    </td>
                    <td className="px-6 py-3 text-zinc-900 font-mono text-[12px]">{n.kind}</td>
                    <td className="px-6 py-3 text-zinc-700">{n.account_name}</td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium uppercase tracking-widest ${
                          n.status === "sent"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                      >
                        {n.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-zinc-900 text-[12px]">{formatTimestamp(n.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[11px] text-zinc-900 px-6 py-3 border-t border-zinc-100">
            Resend transactional emails not yet wired. Rows sit here until the Phase 4 worker drains them.
          </p>
        </section>

        {/* Waitlist (Wave 10) */}
        <section className="bg-white border border-zinc-200 mt-8" data-testid="admin-waitlist-section">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-zinc-950 font-heading">Waitlist</h2>
              <p className="text-[12px] text-zinc-900 mt-0.5">Paid-intent signal captured ahead of Stripe launch.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 text-[12px]" data-testid="admin-waitlist-counts">
                <span className="text-zinc-900">Total: <span className="font-medium text-zinc-950">{waitlistCounts.total}</span></span>
                <span className="text-zinc-900">Monthly: <span className="font-medium text-amber-700">{waitlistCounts.monthly}</span></span>
                <span className="text-zinc-900">Annual: <span className="font-medium text-emerald-700">{waitlistCounts.annual}</span></span>
              </div>
              <Button
                onClick={handleExportWaitlistCSV}
                disabled={waitlistCounts.total === 0}
                variant="outline"
                className="rounded-none border-zinc-200 hover:bg-zinc-50 text-sm font-medium disabled:opacity-40"
                data-testid="admin-waitlist-export-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                Export CSV
              </Button>
            </div>
          </div>
          {waitlist.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-zinc-900" data-testid="admin-waitlist-empty">
              No waitlist entries yet. Trial users will land here as they reserve seats.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200">
                <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-900">
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Pref.</th>
                  <th className="px-6 py-3 font-medium">Vendor</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Seniority</th>
                  <th className="px-6 py-3 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map((w, i) => {
                  const p = w.profile_snapshot || {};
                  return (
                    <tr key={i} className="border-b border-zinc-100 last:border-0" data-testid={`admin-waitlist-row-${i}`}>
                      <td className="px-6 py-3 text-zinc-950">{w.email}</td>
                      <td className="px-6 py-3 text-zinc-900">{w.first_name || <span className="text-zinc-900">—</span>}</td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium uppercase tracking-widest ${
                            w.pricing_preference === "annual"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {w.pricing_preference}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-zinc-700 text-[12px]">{p.company_type || <span className="text-zinc-900">—</span>}</td>
                      <td className="px-6 py-3 text-zinc-700 text-[12px]">{p.role_family || <span className="text-zinc-900">—</span>}</td>
                      <td className="px-6 py-3 text-zinc-700 text-[12px]">{p.seniority_tier || <span className="text-zinc-900">—</span>}</td>
                      <td className="px-6 py-3 text-zinc-900 text-[12px]">{formatTimestamp(w.submitted_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* Beta admin onboarding — Add account modal */}
      <AddAccountModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={async () => { await refresh(); }}
        existingSlugs={accounts.map((a) => a.id)}
      />

      {/* v6.5.1 — Remove account modal (soft-archive) */}
      <RemoveAccountModal
        open={removeModalOpen}
        onOpenChange={setRemoveModalOpen}
        account={accounts.find((a) => a.id === selectedAccountId) || null}
        healthEntry={health[selectedAccountId] || null}
        onSuccess={async () => {
          // Clear selection + refresh the table so the archived row disappears.
          setSelectedAccountId(null);
          await refresh();
        }}
      />
    </div>
  );
}
